import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import SaveIcon from '@mui/icons-material/Save'
import LoadingButton from '@mui/lab/LoadingButton'
import { Alert, Box } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Form, FormControlledSwitchLabeled, Select, TextField } from '@ui/forms'
import { NestedMenuItem } from '@ui/navigation/NestedMenu'
import { useSnackbar } from '@ui/snackbar'
import { useResultsInvalidation } from '@editorHooks'
import { getBackendOtherSettings, getPdfPlanSettings } from '@queries'
import { postPdfPlanSettings, setBackendOtherSettings } from '@mutations'
import { buildErrorMessage } from 'src/constants'
import { projectSettingsFormSchema, stiffeningSegmentStoreyAssignmentOptions } from './schema'

type ProjectSettingsProps = {
  parentMenuOpen: boolean
}

function ProjectSettings({ parentMenuOpen }: ProjectSettingsProps) {
  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const queryClient = useQueryClient()
  const invalidateResults = useResultsInvalidation()

  const { data: otherSettings, error: otherSettingsError } = useQuery({
    queryKey: getBackendOtherSettings.getKey(projectId),
    queryFn: () => getBackendOtherSettings.request(projectId),
    enabled: !!projectId,
  })

  const { data: planSettings, error: planSettingsError } = useQuery({
    queryKey: getPdfPlanSettings.getKey(projectId),
    queryFn: () => getPdfPlanSettings.request(projectId),
    enabled: !!projectId,
  })

  useEffect(() => {
    if (otherSettingsError) {
      enqueueSnackbar('Fehler beim Laden der Einstellungen fürs Backend.', {
        variant: 'error',
      })
    }
  }, [otherSettingsError, enqueueSnackbar])

  useEffect(() => {
    if (planSettingsError) {
      enqueueSnackbar('Fehler beim Laden der Plan-Einstellungen.', {
        variant: 'error',
      })
    }
  }, [planSettingsError, enqueueSnackbar])

  const { mutateAsync: saveBackendOtherSettingsMutation, isLoading: isSavingOtherSettings } =
    useMutation((data: BackendOtherSettings) => setBackendOtherSettings.request(projectId, data), {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getBackendOtherSettings.getKey(projectId))
        // These settings feed the computation, so the backend resets the results.
        await invalidateResults(projectId as string)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Backend-Einstellungen'),
          {
            variant: 'error',
          },
        )
      },
    })

  const { mutateAsync: savePlanSettingsMutation, isLoading: isSavingPlanSettings } = useMutation(
    (settings: PdfPlanSettings) => postPdfPlanSettings.request(projectId, settings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getPdfPlanSettings.getKey(projectId))
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Plan-Einstellungen'), {
          variant: 'error',
        })
      },
    },
  )

  if (!projectId) {
    return null
  }

  return (
    <NestedMenuItem
      label="Einstellungen"
      parentMenuOpen={parentMenuOpen}
      direction="left"
      leftIcon={<ChevronLeftIcon />}
      data-cy="project-settings-menu"
    >
      {otherSettings && planSettings && (
        <>
          <Form
            onSubmit={async values => {
              const { stiffeningSegmentStoreyAssignment, ...backendSettings } = values
              await saveBackendOtherSettingsMutation(backendSettings)
              await savePlanSettingsMutation({
                ...planSettings,
                stiffeningSegmentStoreyAssignment,
              })
              enqueueSnackbar('Einstellungen erfolgreich gespeichert', {
                variant: 'success',
              })
            }}
            validationSchema={projectSettingsFormSchema}
            defaultValues={{
              ...otherSettings,
              stiffeningSegmentStoreyAssignment: planSettings.stiffeningSegmentStoreyAssignment,
            }}
            enableReinitialize
          >
            <MenuItem>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  label="Auflagerreaktionen-Check"
                  placeholder="0.001"
                  tooltip={{
                    text: 'Toleranz für Check ob Summe der Auflagerreaktionen und Summe der Lasten gleich sind',
                    headline: 'Summenreaktionen-Check',
                  }}
                  type="number"
                  size="small"
                  name="load_transmission.sum_reactions_validation_abs_tol"
                  unit="N"
                  sx={{ minWidth: 200 }}
                  data-cy="backend-other-settings-sum-reactions-validation-abs-tol-input"
                />
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Ergebnisse
                </Typography>
                <FormControlledSwitchLabeled
                  name="loads_for_checks.decisive_positioning_of_changing_loads"
                  label="Maßgebende Laststellung veränderlicher Lasten (empfohlen)"
                  tooltip={{
                    headline: 'Maßgebende Laststellung veränderlicher Lasten',
                    text: 'Wenn aktiviert, werden Nutz- und Schneelasten für die Nachweise von Durchlaufträgern feldweise angeordnet, um die ungünstigste Laststellung zu finden (DIN EN 1991-1-1, 6.2.1). Betrifft nur die Bauteilnachweise, nicht die Lastweiterleitung. Für neue Projekte standardmäßig aktiv; in älteren Projekten ausgeschaltet, damit deren Ergebnisse unverändert bleiben, bis die Funktion hier aktiviert wird.',
                  }}
                  data-cy="backend-other-settings-decisive-positioning-of-changing-loads"
                />
                <TextField
                  label="Felder mit allen Laststellungen"
                  type="number"
                  size="small"
                  name="loads_for_checks.max_fields_for_all_patterns"
                  tooltip={{
                    headline: 'Felder mit allen Laststellungen',
                    text: 'Bis zu dieser Anzahl belasteter Felder werden alle Kombinationen aus belasteten und unbelasteten Feldern untersucht (2ⁿ−1 Laststellungen je veränderlicher Einwirkung). Bei mehr Feldern nur die klassischen Laststellungen: jedes zweite Feld, zwei benachbarte Felder, einzelne Felder und Vollbelastung.',
                  }}
                  inputProps={{ min: 1, max: 8, step: 1 }}
                  sx={{ minWidth: 200 }}
                  data-cy="backend-other-settings-max-fields-for-all-patterns-input"
                />
                <Select
                  name="supportAreaForRips"
                  label="Auflagerfläche bei Rippen"
                  options={[
                    {
                      value: 'fromWall',
                      label: 'Wandgeometrie (UZ Breite)',
                    },
                    {
                      value: 'fromRip',
                      label: 'Rippengeometrie (min(UZ Breite, Rippe))',
                    },
                  ]}
                  tooltip={{
                    headline: 'Auflagerfläche bei Rippen',
                    text: 'Steuert, wie die Auflagerfläche für Drucknachweise berechnet wird, wenn Träger/Pfetten auf Rippen aufliegen: Wandgeometrie = Balkenbreite × Wanddicke; Rippengeometrie = min(Balkenbreite, Rippenbreite) × Rippenlänge.',
                  }}
                  sx={{ minWidth: 200 }}
                  data-cy="backend-other-settings-support-area-strategy-for-rips"
                />
                <FormControlledSwitchLabeled
                  name="includeRipAppliedLoadsInTieDownAnchorUplift"
                  label="Lasten auf Rippen für Zuganker berücksichtigen (empfohlen)"
                  tooltip={{
                    headline: 'Lasten auf Rippen für Zuganker berücksichtigen',
                    text: 'Es wird empfohlen diese Funktion zu aktivieren, und nur für alte Projekte zum Erhalt der vorherigen Ergebnisse zu deaktivieren. Wenn aktiviert werden Lasten, die direkt auf Rippen und nicht über die Wand geleitet werden, für die Stabilisierung der Zuganker berücksichtigt. Wenn deaktiviert werden nur Lasten als stabilisierend angesetzt, die direkt über die Wand geleitet werden. Beispiel: Ein Unterzug leitet seine Lasten direkt auf eine Wandrippe ein und die Funktion ist aktiviert. Dann werden stabilisierende Lasten des Unterzugs für die Wand berücksichtigt. Ist die Funktion deaktiviert, werden diese nicht berücksichtigt. ',
                  }}
                  data-cy="backend-other-settings-include-rip-loads-in-tie-down-anchor-uplift"
                />
                <TextField
                  label="Reibungsbeiwert μ (Schubanker)"
                  type="number"
                  size="small"
                  name="shear_anchor_settings.friction_coefficient"
                  tooltip={{
                    headline: 'Reibungsbeiwert der Gleitreibung',
                    text: 'Gleitreibungsbeiwert zwischen Wand und Untergrund für den Schubankernachweis. Die vertikalen Auflasten (ohne Wind und Erdbeben) reduzieren den Schubfluss um μ × Auflast. Zulässig sind Werte von 0 bis 1; 0 bedeutet keine Reibung (z. B. Wand auf Folie), Standard 0,25. Eine Änderung setzt die Ergebnisse zurück.',
                  }}
                  inputProps={{ min: 0, max: 1, step: 0.01 }}
                  sx={{ minWidth: 200 }}
                  data-cy="backend-other-settings-shear-anchor-friction-coefficient-input"
                />
                <Select
                  name="stiffeningSegmentStoreyAssignment"
                  label="Stockwerkszuweisung für aussteifende Segmente"
                  options={[...stiffeningSegmentStoreyAssignmentOptions]}
                  sx={{ minWidth: 200 }}
                  data-cy="project-settings-stiffening-segment-storey-assignment"
                />
                <Alert severity="warning" sx={{ py: 0 }}>
                  Diese Einstellung betrifft nur die Planerstellung und nicht den Abschnitt
                  &quot;Verteilung Aussteifungslasten&quot; im Dokumentemanager.
                </Alert>
                <LoadingButton
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="small"
                  fullWidth
                  startIcon={<SaveIcon />}
                  data-cy="backend-other-settings-save-button"
                  loading={isSavingOtherSettings || isSavingPlanSettings}
                >
                  Speichern
                </LoadingButton>
              </Box>
            </MenuItem>
          </Form>
        </>
      )}
    </NestedMenuItem>
  )
}

export default ProjectSettings
